'use client';

import { useCallback, useRef } from 'react';
import { Heart, MessageCircle, Copy, Star } from 'lucide-react';
import type { CommunityPost } from '@/lib/community/types';
import { formatCount } from '@/lib/community/utils';
import { spawnEmojiFirework } from '@/lib/emoji-firework';
import { AuthorLink } from './author-link';
import { useToast } from '@/lib/hooks/use-toast';
import { ApiError } from '@/lib/api/client';

/**
 * PostCardStats — shared bottom stats row for all card variants.
 * Optimistic update in service layer; state lock prevents double-click.
 */
export function PostCardStats({
	post,
	onLike,
	onFavorite,
}: {
	post: CommunityPost;
	onLike?: (post: CommunityPost) => Promise<void>;
	onFavorite?: (post: CommunityPost) => Promise<void>;
}) {
	const likePending = useRef(false);
	const favPending = useRef(false);
	const toast = useToast();

	const handleLike = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			if (likePending.current) return;
			likePending.current = true;
			if (!post.isLiked) {
				const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
				spawnEmojiFirework(rect.left + rect.width / 2, rect.top + rect.height / 2, '❤️');
			}
			try {
				await onLike?.(post);
			} catch (err) {
				if (err instanceof ApiError && err.status === 429) {
					toast.error('操作太频繁，请稍后再试');
				}
			} finally {
				likePending.current = false;
			}
		},
		[onLike, post, toast],
	);

	const handleFavorite = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			if (favPending.current) return;
			favPending.current = true;
			if (!post.isFavorited) {
				const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
				spawnEmojiFirework(rect.left + rect.width / 2, rect.top + rect.height / 2, '⭐');
			}
			try {
				await onFavorite?.(post);
			} catch (err) {
				if (err instanceof ApiError && err.status === 429) {
					toast.error('操作太频繁，请稍后再试');
				}
			} finally {
				favPending.current = false;
			}
		},
		[onFavorite, post, toast],
	);

	return (
		<div className="flex items-center justify-between mt-auto pt-1">
			<AuthorLink authorId={post.author.id} avatar={post.author.avatar} displayName={post.author.displayName} />

			<div className="flex items-center gap-2 text-[11px] text-muted-foreground shrink-0">
				<button className="relative inline-flex items-center gap-0.5 hover:text-red-500 transition-colors" onClick={handleLike} type="button">
					<Heart className={`w-3 h-3 ${post.isLiked ? 'fill-red-500 text-red-500' : ''}`} />
					{formatCount(post.stats.likes)}
				</button>

				<button className="relative inline-flex items-center gap-0.5 hover:text-amber-500 transition-colors" onClick={handleFavorite} type="button">
					<Star className={`w-3 h-3 ${post.isFavorited ? 'fill-amber-500 text-amber-500' : ''}`} />
					{formatCount(post.stats.favorites)}
				</button>

				<span className="inline-flex items-center gap-0.5">
					<MessageCircle className="w-3 h-3" />
					{formatCount(post.stats.comments)}
				</span>

				{post.stats.copies > 0 && (
					<span className="inline-flex items-center gap-0.5">
						<Copy className="w-3 h-3" />
						{formatCount(post.stats.copies)}
					</span>
				)}
			</div>
		</div>
	);
}
